import { hasPermission, type AuthUser, type PermissionAction } from '../types/roles';

export type RoutePolicy = 'public' | 'authenticated' | `permission:${PermissionAction}`;
export type RoutePolicyRequirement = RoutePolicy | readonly RoutePolicy[];

export interface RoutePolicyEntry {
  route: string;
  policy: RoutePolicyRequirement;
  notes?: string;
}

/**
 * Canonical frontend route-access manifest.
 * Every non-public route in the active app router must be listed here.
 */
export const ROUTE_POLICY_MANIFEST = [
  { route: '/login', policy: 'public' },
  { route: '/register', policy: 'public' },
  { route: '/registration-links/:token', policy: 'public' },
  { route: '/invitations/:invitationId', policy: 'public' },
  { route: '/forgot-password', policy: 'public' },
  { route: '/reset-password', policy: 'public' },
  { route: '/verify-email', policy: 'public' },
  { route: '/forbidden', policy: 'public' },

  { route: '/', policy: 'authenticated' },
  { route: '/dashboard', policy: 'permission:project.view' },
  { route: '/settings/password', policy: 'authenticated' },
  { route: '/settings/email', policy: 'authenticated' },
  { route: '/change-password', policy: 'authenticated' },

  { route: '/partners', policy: 'permission:agency.manage_assignments' },
  { route: '/cam-dashboard', policy: 'authenticated', notes: 'CAM Dashboard for operators (agency_operator, org_admin, campaign_manager).' },
  { route: '/audit-log', policy: 'permission:analytics.view_audit' },
  { route: '/platform-admin', policy: 'permission:platform.manage_orgs' },

  { route: '/data-foundation', policy: 'authenticated' },
  { route: '/campaigns', policy: 'authenticated' },
  { route: '/campaigns/create', policy: 'authenticated' },
  { route: '/campaigns/:campaignId/generation', policy: 'authenticated' },
  { route: '/campaigns/:campaignId/collaborators', policy: 'permission:campaign.manage_collaborators' },
  { route: '/campaigns/:campaignId/export', policy: 'authenticated' },
  { route: '/campaigns/:campaignId/citation-audit', policy: 'authenticated' },
  { route: '/campaigns/:campaignId/trust-report', policy: 'authenticated' },

  { route: '/solutions', policy: 'authenticated' },
  { route: '/solutions/:id', policy: 'authenticated' },

  { route: '/upload-center', policy: 'authenticated' },
  { route: '/upload', policy: 'authenticated' },
  { route: '/knowledge-graph-status', policy: 'authenticated' },
  { route: '/projects', policy: 'authenticated' },
  { route: '/project-setup', policy: 'authenticated' },

  { route: '/admin', policy: 'permission:org.manage_ai_config' },
  { route: '/admin/llm-settings', policy: 'permission:org.manage_ai_config' },
  { route: '/admin/walkthrough-analytics', policy: 'permission:analytics.view_audit' },

  { route: '/templates', policy: 'authenticated' },
  { route: '/pipeline-visualization', policy: 'authenticated' },

  { route: '/workflow', policy: 'authenticated' },
  { route: '/workflow/:step', policy: 'authenticated' },
  { route: '/workflow/:campaignId/:step', policy: 'authenticated' },
  { route: '/campaign/:campaignId/workflow', policy: 'authenticated' },
  { route: '/campaign/:campaignId/workflow/:step', policy: 'authenticated' },

  { route: '/knowledge', policy: 'authenticated' },
  { route: '/knowledge/review', policy: 'permission:content.approve' },
  { route: '/classification/review-queue', policy: 'permission:content.approve' },
  { route: '/classification/settings', policy: 'permission:content.approve' },
  { route: '/content-sufficiency', policy: 'permission:analytics.view' },

  { route: '/campaign-builder', policy: 'authenticated' },
  { route: '/content-flow', policy: 'authenticated' },
  { route: '/analytics', policy: 'permission:analytics.view' },
  { route: '/export', policy: 'authenticated' },

  { route: '/health', policy: 'permission:platform.database_ops' },
  { route: '/agent-coordination', policy: 'authenticated' },
  { route: '/command-center', policy: 'permission:agent.execute' },
  { route: '/task-board', policy: 'permission:campaign.view' },

  { route: '/orchestration', policy: 'authenticated' },
  { route: '/agents', policy: 'authenticated' },
  { route: '/agents/discover', policy: 'authenticated' },
  { route: '/discover-agents', policy: 'authenticated' },
  { route: '/sofia', policy: 'authenticated' },
  { route: '/casey', policy: 'authenticated' },
  { route: '/zara', policy: 'authenticated' },

  { route: '/settings', policy: 'permission:org.edit_settings' },
  { route: '/settings/team', policy: 'permission:user.view_list' },
  { route: '/settings/billing', policy: ['permission:org.view_billing', 'permission:subscription.view'] },
  { route: '/settings/usage', policy: ['permission:credit.view_usage', 'permission:credit.view_partner_usage'] },
  { route: '/settings/upgrade', policy: ['permission:org.change_plan', 'permission:subscription.manage'] },
  { route: '/settings/integrations', policy: 'permission:org.edit_settings' },
  { route: '/subscription', policy: 'authenticated' },
  { route: '/usage', policy: 'authenticated' },
  { route: '/upgrade', policy: 'authenticated' },
  { route: '/profile', policy: 'authenticated' },
  { route: '/content-generation', policy: 'authenticated' },
  { route: '/performance-analytics', policy: 'permission:analytics.view' },

  { route: '*', policy: 'authenticated', notes: 'Catch-all authenticated fallback route.' },
] as const satisfies readonly RoutePolicyEntry[];

const normalizePathname = (pathname: string): string => {
  if (!pathname) {
    return '/';
  }

  const normalized = pathname.trim();
  if (!normalized.startsWith('/')) {
    return `/${normalized}`;
  }

  if (normalized.length > 1 && normalized.endsWith('/')) {
    return normalized.slice(0, -1);
  }

  return normalized;
};

const escapeRegex = (value: string): string => value.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&');

const routeTemplateToRegex = (routeTemplate: string): RegExp => {
  if (routeTemplate === '*') {
    return /^.*$/;
  }

  const normalized = normalizePathname(routeTemplate);
  const segments = normalized.split('/').filter(Boolean);

  if (segments.length === 0) {
    return /^\/$/;
  }

  const regexSegments = segments.map((segment) => {
    if (segment.startsWith(':')) {
      return '[^/]+';
    }
    return escapeRegex(segment);
  });

  return new RegExp(`^/${regexSegments.join('/')}$`);
};

const ROUTE_POLICY_WITH_REGEX = ROUTE_POLICY_MANIFEST.map((entry) => ({
  ...entry,
  regex: routeTemplateToRegex(entry.route),
}));

export function resolveRoutePolicy(pathname: string): RoutePolicyEntry | null {
  const normalizedPath = normalizePathname(pathname);
  const matched = ROUTE_POLICY_WITH_REGEX.find((entry) => entry.regex.test(normalizedPath));
  if (!matched) {
    return null;
  }

  return {
    route: matched.route,
    policy: matched.policy,
    notes: 'notes' in matched ? matched.notes : undefined,
  };
}

export function getRoutePolicyForPath(pathname: string): RoutePolicyRequirement {
  return resolveRoutePolicy(pathname)?.policy ?? 'authenticated';
}

export function resolveRoutePolicyByRoute(route: string): RoutePolicyEntry | null {
  const matched = ROUTE_POLICY_MANIFEST.find((entry) => entry.route === route);
  return matched ?? null;
}

const normalizePolicies = (policy: RoutePolicyRequirement): readonly RoutePolicy[] =>
  Array.isArray(policy) ? policy : [policy];

export function getPolicyPermission(policy: RoutePolicyRequirement): PermissionAction | null {
  if (Array.isArray(policy)) {
    return null;
  }
  if (!policy.startsWith('permission:')) {
    return null;
  }

  return policy.replace('permission:', '') as PermissionAction;
}

export function isAuthorizedForPolicy(
  user: AuthUser | null,
  policy: RoutePolicyRequirement,
): boolean {
  return normalizePolicies(policy).some((entry) => {
    if (entry === 'public') {
      return true;
    }

    if (!user) {
      return false;
    }

    if (entry === 'authenticated') {
      return true;
    }

    const permission = getPolicyPermission(entry);
    if (!permission) {
      return false;
    }

    return hasPermission(user.role, permission);
  });
}

export function isAuthorizedForPath(user: AuthUser | null, pathname: string): boolean {
  return isAuthorizedForPolicy(user, getRoutePolicyForPath(pathname));
}

export const PUBLIC_ROUTE_PATHS = ROUTE_POLICY_MANIFEST.filter(
  (entry) => entry.policy === 'public',
).map((entry) => entry.route);

export const PROTECTED_ROUTE_PATHS = ROUTE_POLICY_MANIFEST.filter(
  (entry) => entry.policy !== 'public',
).map((entry) => entry.route);
